import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { authApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('La password deve avere almeno 8 caratteri.'); return; }
    if (password !== confirm) { setError('Le password non coincidono.'); return; }
    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Errore durante il reset.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2"><Mail className="h-6 w-6 text-primary" /><span className="font-bold text-lg">Blagomail</span></div>
          <CardTitle>Reimposta password</CardTitle>
          <CardDescription>Scegli una nuova password per il tuo account.</CardDescription>
        </CardHeader>
        <CardContent>
          {!token ? (
            <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">
              Link non valido. Richiedi un nuovo link da <Link to="/forgot-password" className="underline">qui</Link>.
            </div>
          ) : done ? (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
              <p className="text-sm">Password aggiornata! Ti reindirizzo al login…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pw">Nuova password</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpw">Conferma password</Label>
                <Input id="cpw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </div>
              {error && <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{error}</div>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Imposta nuova password
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
